// ── BCH Radio — Community Social Music Platform ──────────────────
//
// A Nostr-native community music platform. Artists upload songs,
// listeners discover music, and creators earn BCH through direct tips.
//
// Event Kinds:
//   30078  – Station state (addressable, d-tag = "bchnostr/radio/<stationId>")
//            Content: JSON with current playlist, now-playing index, etc.
//   1808   – Track submission (regular event)
//            A user submits a track to a station's queue.
//   7      – Track reaction (like a track)
//   1      – Track post (auto-posted to feed when artist uploads)
//   30078  – Playlist (addressable, d-tag = "bchnostr/playlist/<playlistId>")
//
// Payments:
//   Queue fee: configurable per station (default $0.01) — paid to platform
//   Skip fee: pay extra to bump your track up in the queue
//   Tips: listeners can tip the track's artist directly

import { PLATFORM_FEE_ADDRESS } from './platform';

// ── Constants ────────────────────────────────────────────────────

export const RADIO_STATION_KIND = 30078;
export const RADIO_TRACK_KIND = 1808;
export const RADIO_PLAYLIST_KIND = 30078;
export const RADIO_STATION_TAG = 'bch-radio-station';
export const RADIO_TRACK_TAG = 'bch-radio-track';
export const RADIO_PLAYLIST_TAG = 'bch-radio-playlist';
export const RADIO_WALLET = PLATFORM_FEE_ADDRESS;

/** Default station ID for the global BCH Radio */
export const DEFAULT_STATION_ID = 'bch-radio-main';

/** d-tag prefix for station state events */
export const STATION_D_TAG_PREFIX = 'bchnostr/radio/';

/** d-tag prefix for playlist events */
export const PLAYLIST_D_TAG_PREFIX = 'bchnostr/playlist/';

/** Minimum queue fee in USD */
export const MIN_QUEUE_FEE_USD = 0.01;

/** Default queue fee in USD */
export const DEFAULT_QUEUE_FEE_USD = 0.01;

/** Fee to skip ahead in queue (per position) in USD */
export const SKIP_FEE_USD = 0.05;

/** Maximum track duration in seconds (10 minutes) */
export const MAX_TRACK_DURATION = 600;

/** Maximum tracks in queue */
export const MAX_QUEUE_SIZE = 50;

/** LocalStorage key for "Soundtrack My Feed" toggle */
export const SOUNDTRACK_FEED_KEY = 'bchnostr:soundtrack-feed';

/** LocalStorage key for play history */
export const PLAY_HISTORY_KEY = 'bchnostr:radio-history';

// ── Promotions (Auction-Style) ────────────────────────────────────
//
// Promotions use kind 30078 (application-specific data) with a d-tag
// prefix of "bchnostr/radio-promo/". Each promotion is an addressable
// event published by the user, storing the promotion details in JSON.
//
// Promotion types:
//   - "song"    — Promote a specific track (appears in Featured Songs)
//   - "artist"  — Featured Artist spot at the top of BCH Radio
//
// AUCTION MODEL:
//   Both Featured Artist and Song Promotion use a competitive auction.
//   Users place bids in BCH. The highest bidder wins the slot.
//   - Featured Artist: 1 slot, 7-day duration. Highest bid wins.
//     To claim the spot, you must outbid the current holder.
//   - Song Promotion: Up to 5 slots, 7-day duration per slot.
//     If all slots are full, you must outbid the lowest current bid.
//
// Payment: BCH sent to the platform wallet. The txid is recorded
//          in the event for verification.

export const RADIO_PROMO_KIND = 30078;
export const RADIO_PROMO_TAG = 'bch-radio-promo';
export const PROMO_D_TAG_PREFIX = 'bchnostr/radio-promo/';

/** Duration for all auction-based promotion slots: 7 days */
export const PROMO_AUCTION_DURATION = 7 * 86400;

/** Maximum promoted song slots (auction style) */
export const PROMO_MAX_SONG_SLOTS = 5;

/** Minimum bid in USD when no one is currently promoted */
export const PROMO_MIN_BID_USD = 0.05; // $0.05

/** Minimum bid in USD for featured artist when no one holds the slot */
export const FEATURED_ARTIST_MIN_BID_USD = 0.10; // $0.10

/** Minimum bid in sats when no one is currently promoted (fallback if no price) */
export const PROMO_MIN_BID_SATS = 5000;

/** Minimum bid for featured artist when no one holds the slot (fallback if no price) */
export const FEATURED_ARTIST_MIN_BID_SATS = 10000;

// ── USD ↔ Sats conversion ────────────────────────────────────────

/** Convert USD to BCH sats. Returns 0 if bchPrice unknown. */
export function usdToSats(usd: number, bchPrice: number): number {
  if (bchPrice <= 0 || usd <= 0) return 0;
  return Math.ceil((usd / bchPrice) * 1e8);
}

/** Convert BCH sats to USD. Returns 0 if bchPrice unknown. */
export function satsToUsd(sats: number, bchPrice: number): number {
  if (bchPrice <= 0 || sats <= 0) return 0;
  return (sats / 1e8) * bchPrice;
}

/** Format USD for display: e.g. 0.05 → "$0.05", 1.5 → "$1.50" */
export function formatUsd(usd: number): string {
  return `$${usd.toFixed(2)}`;
}

/** Legacy tier arrays kept for backwards compat with existing events */
export const PROMO_TIERS = [
  { label: '24 Hours', durationSecs: 86400, priceUsd: 0.25 },
  { label: '3 Days', durationSecs: 86400 * 3, priceUsd: 0.50 },
  { label: '7 Days', durationSecs: 86400 * 7, priceUsd: 5.00 },
] as const;

export const FEATURED_ARTIST_TIERS = [
  { label: '24 Hours', durationSecs: 86400, priceUsd: 0.50 },
  { label: '3 Days', durationSecs: 86400 * 3, priceUsd: 1.00 },
  { label: '7 Days', durationSecs: 86400 * 7, priceUsd: 5.00 },
] as const;

export interface RadioPromotion {
  /** Promotion type */
  type: 'song' | 'artist';
  /** Track event ID (for song promo) or empty (for artist promo) */
  trackId: string;
  /** Pubkey of the promoter (artist) */
  pubkey: string;
  /** Unix timestamp when the promotion expires */
  expiresAt: number;
  /** Unix timestamp when the promotion was created */
  createdAt: number;
  /** Amount paid in sats */
  paidSats: number;
  /** BCH transaction ID (proof of payment) */
  txid: string;
  /** Operator-awarded (no payment required) */
  awarded?: boolean;
}

/** Parse a promotion event */
export function parsePromotion(event: any): RadioPromotion | null {
  try {
    const dTag = event.tags?.find((t: string[]) => t[0] === 'd')?.[1] || '';
    if (!dTag.startsWith(PROMO_D_TAG_PREFIX)) return null;

    const data = JSON.parse(event.content);
    if (!data.type || !data.expiresAt) return null;

    return {
      type: data.type,
      trackId: data.trackId || '',
      pubkey: event.pubkey,
      expiresAt: data.expiresAt,
      createdAt: event.created_at,
      paidSats: data.paidSats || 0,
      txid: data.txid || '',
      awarded: data.awarded || false,
    };
  } catch {
    return null;
  }
}

// ── Auction State Computation ────────────────────────────────────

export interface SongPromoAuctionState {
  /** Active song promo bids sorted by paidSats descending */
  bids: RadioPromotion[];
  /** Number of slots currently occupied */
  slotsUsed: number;
  /** True if all PROMO_MAX_SONG_SLOTS are taken */
  slotsFull: boolean;
  /** Minimum sats required to claim a featured song slot right now */
  minimumBidSats: number;
  /** The lowest current bid (the one that would be outbid), or null */
  lowestBid: RadioPromotion | null;
}

/** Compute the song promotion auction state from active promotions. */
export function computeSongPromoAuction(activePromos: RadioPromotion[], bchPrice?: number): SongPromoAuctionState {
  // Only song promos
  const songBids = activePromos.filter(p => p.type === 'song' && p.trackId);

  // Dedupe by trackId — keep the highest bid per track
  const byTrack = new Map<string, RadioPromotion>();
  for (const bid of songBids) {
    const existing = byTrack.get(bid.trackId);
    if (!existing || bid.paidSats > existing.paidSats) {
      byTrack.set(bid.trackId, bid);
    }
  }

  const bids = [...byTrack.values()].sort((a, b) => b.paidSats - a.paidSats);
  const slotsUsed = bids.length;
  const slotsFull = slotsUsed >= PROMO_MAX_SONG_SLOTS;
  const lowestBid = slotsFull ? bids[bids.length - 1] : null;

  // When slots are full, outbid the lowest; otherwise use the minimum USD-based price
  const baseBidSats = bchPrice && bchPrice > 0
    ? usdToSats(PROMO_MIN_BID_USD, bchPrice)
    : PROMO_MIN_BID_SATS;

  const minimumBidSats = slotsFull && lowestBid
    ? lowestBid.paidSats + 1
    : baseBidSats;

  return { bids, slotsUsed, slotsFull, minimumBidSats, lowestBid };
}

export interface FeaturedArtistAuctionState {
  /** The current winning bid, or null if no one holds the slot */
  currentBid: RadioPromotion | null;
  /** Minimum sats to claim or outbid the featured artist slot */
  minimumBidSats: number;
  /** Hours remaining on the current bid */
  hoursLeft: number;
}

/** Compute the featured artist auction state. */
export function computeFeaturedArtistAuction(activePromos: RadioPromotion[], bchPrice?: number): FeaturedArtistAuctionState {
  const artistBids = activePromos.filter(p => p.type === 'artist');

  // Highest bid wins the single slot
  let currentBid: RadioPromotion | null = null;
  for (const bid of artistBids) {
    if (!currentBid || bid.paidSats > currentBid.paidSats || (bid.paidSats === currentBid.paidSats && bid.createdAt > currentBid.createdAt)) {
      currentBid = bid;
    }
  }

  const now = Math.floor(Date.now() / 1000);
  const hoursLeft = currentBid ? Math.max(0, Math.floor((currentBid.expiresAt - now) / 3600)) : 0;

  const baseBidSats = bchPrice && bchPrice > 0
    ? usdToSats(FEATURED_ARTIST_MIN_BID_USD, bchPrice)
    : FEATURED_ARTIST_MIN_BID_SATS;

  const minimumBidSats = currentBid
    ? currentBid.paidSats + 1
    : baseBidSats;

  return { currentBid, minimumBidSats, hoursLeft };
}

// Supported audio formats
export const AUDIO_EXTENSIONS = /\.(mp3|m4a|ogg|opus|wav|flac|aac|webm)(\?.*)?$/i;
export const AUDIO_MIME_TYPES = [
  'audio/mpeg', 'audio/mp3', 'audio/mp4', 'audio/ogg',
  'audio/opus', 'audio/wav', 'audio/flac', 'audio/aac',
  'audio/webm', 'audio/x-m4a',
];

// ── Genres ────────────────────────────────────────────────────────

export const GENRES = [
  'All',
  'Lo-Fi',
  'Hip Hop',
  'Electronic',
  'Ambient',
  'Rock',
  'Jazz',
  'Classical',
  'R&B',
  'Pop',
  'Country',
  'Reggae',
  'Latin',
  'Folk',
  'Podcast',
  'Spoken Word',
  'Other',
] as const;

export type Genre = (typeof GENRES)[number];

// ── Types ────────────────────────────────────────────────────────

export interface RadioTrack {
  /** Unique ID (event ID of the track submission) */
  id: string;
  /** Audio file URL (Blossom or any public URL) */
  url: string;
  /** Track title */
  title: string;
  /** Artist / creator name */
  artist: string;
  /** Duration in seconds (0 if unknown) */
  duration: number;
  /** Pubkey of the user who submitted the track */
  submittedBy: string;
  /** Timestamp when submitted */
  submittedAt: number;
  /** Amount paid in sats to queue this track */
  paidSats: number;
  /** Cover art URL (optional) */
  coverArt?: string;
  /** Genre / category */
  genre?: string;
}

/** Social stats for a track (computed client-side from reactions/tips) */
export interface TrackStats {
  likes: number;
  comments: number;
  reposts: number;
  tipTotalSats: number;
  likedByMe: boolean;
  repostedByMe: boolean;
}

/** Playlist — a user-curated collection of tracks */
export interface RadioPlaylist {
  id: string;
  name: string;
  description: string;
  coverArt?: string;
  trackIds: string[];
  createdBy: string;
  createdAt: number;
  updatedAt: number;
}

export interface StationState {
  /** Station identifier */
  stationId: string;
  /** Station display name */
  name: string;
  /** Station description */
  description: string;
  /** Current playlist / queue */
  queue: RadioTrack[];
  /** Index of the currently playing track (-1 if nothing playing) */
  currentIndex: number;
  /** Unix timestamp when the current track started playing */
  trackStartedAt: number;
  /** Station owner pubkey */
  owner: string;
  /** Queue fee in USD */
  queueFeeUsd: number;
  /** Station cover/banner image */
  image?: string;
  /** Total tracks played all time */
  totalPlayed: number;
}

// ── Parsing ──────────────────────────────────────────────────────

/**
 * Parse a station state event (kind 30078 with radio d-tag).
 */
export function parseStationState(event: any): StationState | null {
  try {
    const dTag = event.tags?.find((t: string[]) => t[0] === 'd')?.[1] || '';
    if (!dTag.startsWith(STATION_D_TAG_PREFIX)) return null;

    const data = JSON.parse(event.content);
    return {
      stationId: dTag.replace(STATION_D_TAG_PREFIX, ''),
      name: data.name || 'BCH Radio',
      description: data.description || '',
      queue: Array.isArray(data.queue) ? data.queue : [],
      currentIndex: typeof data.currentIndex === 'number' ? data.currentIndex : -1,
      trackStartedAt: typeof data.trackStartedAt === 'number' ? data.trackStartedAt : 0,
      owner: event.pubkey,
      queueFeeUsd: typeof data.queueFeeUsd === 'number' ? data.queueFeeUsd : DEFAULT_QUEUE_FEE_USD,
      image: data.image || undefined,
      totalPlayed: typeof data.totalPlayed === 'number' ? data.totalPlayed : 0,
    };
  } catch {
    return null;
  }
}

/**
 * Parse a track submission event (kind 1808).
 */
export function parseTrackSubmission(event: any): RadioTrack | null {
  try {
    const data = JSON.parse(event.content);
    if (!data.url) return null;

    return {
      id: event.id,
      url: data.url,
      title: data.title || 'Untitled',
      artist: data.artist || 'Unknown',
      duration: typeof data.duration === 'number' ? data.duration : 0,
      submittedBy: event.pubkey,
      submittedAt: event.created_at,
      paidSats: typeof data.paidSats === 'number' ? data.paidSats : 0,
      coverArt: data.coverArt || undefined,
      genre: data.genre || undefined,
    };
  } catch {
    return null;
  }
}

/**
 * Parse a playlist event (kind 30078 with playlist d-tag).
 */
export function parsePlaylist(event: any): RadioPlaylist | null {
  try {
    const dTag = event.tags?.find((t: string[]) => t[0] === 'd')?.[1] || '';
    if (!dTag.startsWith(PLAYLIST_D_TAG_PREFIX)) return null;

    const data = JSON.parse(event.content);
    return {
      id: dTag,
      name: data.name || 'Untitled Playlist',
      description: data.description || '',
      coverArt: data.coverArt || undefined,
      trackIds: Array.isArray(data.trackIds) ? data.trackIds : [],
      createdBy: event.pubkey,
      createdAt: event.created_at,
      updatedAt: event.created_at,
    };
  } catch {
    return null;
  }
}

// ── Tag Builders ─────────────────────────────────────────────────

/**
 * Build tags for a track submission event.
 */
export function buildTrackSubmissionTags(stationId: string, genre?: string): string[][] {
  const tags: string[][] = [
    ['t', RADIO_TRACK_TAG],
    ['t', 'bch'],
    ['t', 'bch-radio'],
    ['d', `${STATION_D_TAG_PREFIX}${stationId}/track`],
    ['a', `${RADIO_STATION_KIND}:${stationId}`],
  ];
  if (genre && genre !== 'All') {
    tags.push(['t', `bch-radio-genre-${genre.toLowerCase().replace(/[^a-z0-9]/g, '-')}`]);
  }
  return tags;
}

/**
 * Build tags for a station state event.
 */
export function buildStationStateTags(stationId: string): string[][] {
  return [
    ['d', `${STATION_D_TAG_PREFIX}${stationId}`],
    ['t', RADIO_STATION_TAG],
    ['t', 'bch'],
    ['t', 'bch-radio'],
  ];
}

// ── Helpers ──────────────────────────────────────────────────────

/**
 * Format seconds into mm:ss display.
 */
export function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Check if a URL is a valid audio file.
 */
export function isAudioUrl(url: string): boolean {
  return AUDIO_EXTENSIONS.test(url);
}

/**
 * Check if a file is a valid audio file.
 */
export function isAudioFile(file: File): boolean {
  return AUDIO_MIME_TYPES.includes(file.type) || AUDIO_EXTENSIONS.test(file.name);
}

/**
 * Get the elapsed time into the current track.
 */
export function getTrackElapsed(trackStartedAt: number): number {
  if (!trackStartedAt) return 0;
  const now = Math.floor(Date.now() / 1000);
  return Math.max(0, now - trackStartedAt);
}

/**
 * Soundtrack My Feed: read toggle state from localStorage.
 */
export function getSoundtrackEnabled(): boolean {
  try {
    return localStorage.getItem(SOUNDTRACK_FEED_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Soundtrack My Feed: persist toggle state.
 */
export function setSoundtrackEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(SOUNDTRACK_FEED_KEY, enabled ? '1' : '0');
  } catch {}
}

/**
 * Read play history (recent track IDs).
 */
export function getPlayHistory(): string[] {
  try {
    const raw = localStorage.getItem(PLAY_HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/**
 * Add a track ID to play history (max 50, most recent first).
 */
export function addToPlayHistory(trackId: string): void {
  try {
    const history = getPlayHistory().filter(id => id !== trackId);
    history.unshift(trackId);
    localStorage.setItem(PLAY_HISTORY_KEY, JSON.stringify(history.slice(0, 50)));
  } catch {}
}

